import { Alert, Box, Button, Paper, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import logo from '../assets/deco-stop-logo.svg';
import { apiScopes, msalInstance } from '../auth/msal';
import { brand, signIn as ds } from '../theme/brand';

/**
 * Sign-in for the Deco Stop back office.
 *
 * Structure mirrors staff.seahuntdivers.com/sign-in (dark beachfront backdrop, white card,
 * brand mark, provider button), but wears the DECO STOP identity: the dive-helmet crest from
 * decostop.bar and the bar's warm teal/coral/sand palette — not Sea Hunt Divers' navy & gold.
 * The Microsoft button follows Microsoft's identity branding guidelines (light theme).
 */

function MicrosoftMark() {
  // Official four-square mark; 21px keeps it within the 16–24px guideline range at this size.
  //
  // Decorative, and deliberately so: the button it sits inside already reads "Sign in with
  // Microsoft", so a mark that announced itself would say it twice. `aria-hidden="true"` rather
  // than a bare `aria-hidden` — they mean the same thing in JSX, but only the explicit form says
  // "hidden on purpose" to a reader and to the linter.
  return (
    <svg width={21} height={21} viewBox="0 0 23 23" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

export function SignInPage() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await msalInstance.loginRedirect({ scopes: apiScopes, prompt: 'select_account' });
    } catch (err) {
      setBusy(false);
      // Deliberately generic in the UI — detail stays in the console.
      console.error('[signin]', err);
      setError('Sign-in failed. Please try again.');
    }
  };

  return (
    <Box
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        // Beachfront water at dusk: aqua glow over deep teal.
        background: `radial-gradient(1100px 620px at 50% -12%, ${brand.aqua}47, transparent 62%),
          linear-gradient(180deg, ${brand.sea} 0%, ${brand.deep} 52%, ${ds.abyss} 100%)`,
      }}
    >
      <Paper
        radius={18}
        p={36}
        w="100%"
        maw={430}
        style={{
          backgroundColor: '#ffffff',
          border: `1px solid ${brand.haze}`,
          boxShadow: '0 30px 60px -20px rgba(3, 30, 28, 0.55)',
        }}
      >
        <Stack align="center" gap={6} ta="center">
          <img
            src={logo}
            alt="Deco Stop Bar &amp; Grill, Sea Hunt Divers"
            style={{ height: 132, width: 'auto' }}
          />
          <Title
            order={1}
            style={{
              fontFamily: brand.font,
              fontSize: 34,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              lineHeight: 1.05,
              color: brand.deep,
              textTransform: 'uppercase',
            }}
          >
            Operations
          </Title>
          <Text
            size="sm"
            style={{
              fontFamily: brand.font,
              fontWeight: 600,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: brand.sea,
            }}
          >
            Bar &amp; Grill back office
          </Text>
          <Text size="xs" c={ds.muted} mt={2}>
            West End, Roatán
          </Text>
        </Stack>

        <Box
          my={22}
          style={{
            height: 3,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${brand.sea}, ${brand.aqua} 45%, ${brand.gold} 72%, ${brand.coral})`,
          }}
        />

        {error ? (
          // Colors are stated explicitly: the app runs defaultColorScheme="auto", and Mantine's
          // red-light tokens flip to dark translucent surfaces under a dark OS scheme — which
          // would be near-invisible on this always-white card.
          <Alert
            radius="md"
            mb={16}
            title="Sign-in failed"
            role="alert"
            styles={{
              root: { backgroundColor: ds.alertBg, border: '1px solid #fecaca' },
              title: { color: ds.alertTitle },
              message: { color: ds.alertBody },
            }}
          >
            {error}
          </Alert>
        ) : null}

        <Button
          fullWidth
          variant="default"
          onClick={() => void signIn()}
          loading={busy}
          leftSection={<MicrosoftMark />}
          // Microsoft light-theme button: white fill, #8C8C8C border, #5E5E5E label, Segoe UI,
          // ≥41px tall. Driven through Mantine's Button CSS variables rather than `styles`,
          // because `styles` takes plain React style objects — pseudo-selectors there are
          // silently dropped, which would leave hover resolving to Mantine's dark-scheme
          // surface token under a dark OS preference and break the mandated light treatment.
          style={
            {
              '--button-height': '44px',
              '--button-radius': '8px',
              '--button-bg': '#FFFFFF',
              '--button-hover': '#F3F3F3',
              '--button-color': '#5E5E5E',
              '--button-hover-color': '#5E5E5E',
              '--button-bd': '1px solid #8C8C8C',
              fontFamily: '"Segoe UI", ui-sans-serif, system-ui, sans-serif',
              fontWeight: 600,
              fontSize: 15,
            } as React.CSSProperties
          }
        >
          Sign in with Microsoft
        </Button>
      </Paper>
    </Box>
  );
}
